// Account-level billing paths. These are plain strings (not typed `router.buildLocation` targets)
// because the per-tab TanStack routes are migrated in later tickets (SPE-3765–3770); each href
// converts to a typed route reference as its tab lands.
export const BILLING_BASE = '/settings/billing';

export const BILLING_PATHS = {
  overview: `${BILLING_BASE}/overview`,
  yourPlan: `${BILLING_BASE}/your-plan`,
  usage: `${BILLING_BASE}/usage`,
  llmUsage: `${BILLING_BASE}/llm-usage`,
  inboxPreviewUsage: `${BILLING_BASE}/inbox-preview-usage`,
  profileGrowth: `${BILLING_BASE}/profile-growth`,
  billingHistory: `${BILLING_BASE}/billing-history`,
  paymentMethods: `${BILLING_BASE}/payment-methods`,
  billingDetails: `${BILLING_BASE}/billing-details`,
  addOns: `${BILLING_BASE}/add-ons`,
  availablePlans: `${BILLING_BASE}/available-plans/journeys`,
  checkout: `${BILLING_BASE}/checkout`,
};

/**
 * The cards on the Usage tab, in the order they render. A slug names a card in the `open` deep-link
 * parameter and is the card's `data-test-usage-card` value. Shared so a link from another tab opens
 * the card it points at.
 */
export const USAGE_CARD_SLUGS = [
  'profile-usage',
  'email-usage',
  'sms-usage',
  'ai-usage',
  'inbox-preview-usage',
] as const;

export type UsageCardSlug = (typeof USAGE_CARD_SLUGS)[number];

/**
 * The Usage tab, with the named cards opened on load through `?open=`. A literal comma joins the
 * slugs, matching what `nuqs` writes when a reader opens the cards by hand, so a copied link and a
 * built one read the same.
 */
export function buildUsageHref(open: UsageCardSlug[] = []): string {
  if (open.length === 0) {
    return BILLING_PATHS.usage;
  }
  return `${BILLING_PATHS.usage}?open=${open.join(',')}`;
}

/**
 * The query param a completed upgrade hands to the home page so it can celebrate (SPE-3957). It
 * carries the plan that was bought rather than a bare flag, so a later tier gets its own copy
 * instead of inheriting the Essentials wording.
 */
export const UPGRADED_PARAM = 'upgraded';
export const UPGRADED_TO_ESSENTIALS = 'essentials';

/** The workspace-home route a completed upgrade lands on, for `router.buildLocation`. */
export const WORKSPACE_HOME_ROUTE = '/workspaces/$workspaceId/home';
